import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Banknote,
  CheckCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import expenseReimbursementService from '../../services/expenseReimbursementService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ExpenseReimbursement = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalReimbursements: 0,
    pendingReimbursements: 0,
    processedReimbursements: 0,
    paidReimbursements: 0,
    failedReimbursements: 0,
    cancelledReimbursements: 0,
    totalAmount: 0,
    pendingAmount: 0,
    paidAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [approveItem, setApproveItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [reimbursementsResponse, statsResponse] = await Promise.all([
        expenseReimbursementService.getAllReimbursements(),
        expenseReimbursementService.getReimbursementStats()
      ])
      
      if (reimbursementsResponse.success) {
        // Transform _id to id for frontend compatibility
        // Filter to show only Pending reimbursements (approved/processed ones are removed from table)
        const transformedData = (reimbursementsResponse.data || [])
          .filter(reimbursement => reimbursement.status === 'Pending')
          .map(reimbursement => ({
            ...reimbursement,
            id: reimbursement._id
          }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (reimbursementsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No reimbursement records found. Create your first reimbursement!')
        }
      } else {
        toast.error(reimbursementsResponse.message || 'Failed to load reimbursements')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading reimbursement records:', error)
      if (!error._errorHandled) {
        if (error.response?.status === 401) {
          toast.error('Authentication required. Please log in again.')
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to view reimbursements')
        } else {
          toast.error('Failed to load reimbursement records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Pending': 'pending',
      'Processed': 'warning',
      'Paid': 'active',
      'Failed': 'danger',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'reimbursementNumber',
      header: 'Reimbursement Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'claimNumber',
      header: 'Claim Number',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          <span>{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'reimbursementDate',
      header: 'Reimbursement Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'reimbursementAmount',
      header: 'Amount',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue()
        const badgeClass = getStatusBadgeClass(status)
        return (
          <div className="active-cell" style={{ textAlign: 'center' }}>
            <span className={`status-badge ${badgeClass}`}>
              {status || '-'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'paymentMethodName',
      header: 'Payment Method',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          <span>{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'paidDate',
      header: 'Paid Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
  ]

  const handleAdd = () => {
    navigate('/expenses/expense-reimbursement/form')
  }

  const handleEdit = (reimbursementData) => {
    if (reimbursementData.status !== 'Pending') {
      toast.warning('Only pending reimbursements can be edited')
      return
    }
    navigate('/expenses/expense-reimbursement/edit', { state: { reimbursementData } })
  }

  const handleView = (reimbursementData) => {
    navigate('/expenses/expense-reimbursement/view', { state: { reimbursementData } })
  }

  const handleDelete = (reimbursementData) => {
    if (reimbursementData.status !== 'Pending') {
      toast.warning('Only pending reimbursements can be deleted')
      return
    }
    setDeleteItem(reimbursementData)
    setShowDeleteModal(true)
  }

  const handleApprove = (reimbursementData) => {
    if (reimbursementData.status !== 'Pending') {
      toast.warning('Only pending reimbursements can be approved')
      return
    }
    setApproveItem(reimbursementData)
    setShowApproveModal(true)
  }

  const confirmApprove = async () => {
    if (!approveItem) return

    try {
      toast.info('Approving reimbursement...')
      const response = await expenseReimbursementService.processReimbursement(approveItem._id, {})
      if (response.success) {
        toast.success('Reimbursement approved successfully!', { autoClose: 2000 })
        loadData() // Reload data - approved record will be filtered out
      } else {
        toast.error(response.message || 'Failed to approve reimbursement')
      }
    } catch (error) {
      console.error('Error approving reimbursement:', error)
      if (!error._errorHandled) {
        if (error.response?.status === 400) {
          toast.error(error.response.data?.message || 'Cannot approve this reimbursement')
        } else if (error.response?.status === 404) {
          toast.error('Reimbursement not found')
        } else {
          toast.error('Failed to approve reimbursement. Please try again.')
        }
      }
    } finally {
      setShowApproveModal(false)
      setApproveItem(null)
    }
  }

  const cancelApprove = () => {
    setShowApproveModal(false)
    setApproveItem(null)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting reimbursement...')
      const response = await expenseReimbursementService.deleteReimbursement(deleteItem._id)
      if (response.success) {
        toast.success('Reimbursement deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete reimbursement')
      }
    } catch (error) {
      console.error('Error deleting reimbursement:', error)
      if (!error._errorHandled) {
        if (error.response?.status === 400) {
          toast.error(error.response.data?.message || 'Cannot delete this reimbursement')
        } else if (error.response?.status === 404) {
          toast.error('Reimbursement not found')
        } else {
          toast.error('Failed to delete reimbursement. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Reimbursements',
      path: '/expenses/expense-reimbursement',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Reimbursements</h1>
          <p>Payment processing and reimbursement tracking</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Reimbursement
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalReimbursements}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalReimbursements}</div>
          <div className="metric-title">Total Reimbursements</div>
          <div className="metric-subtitle">All reimbursements</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.pendingReimbursements}</span>
            </div>
            <div className="metric-trend positive">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingReimbursements}</div>
          <div className="metric-title">Pending</div>
          <div className="metric-subtitle">Awaiting processing</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.processedReimbursements}</span>
            </div>
            <div className="metric-trend positive">
              <span>Processed</span>
            </div>
          </div>
          <div className="metric-value">{stats.processedReimbursements}</div>
          <div className="metric-title">Processed</div>
          <div className="metric-subtitle">Ready for payment</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{formatCurrency(stats.pendingAmount)}</span>
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.pendingAmount)}</div>
          <div className="metric-title">Pending Amount</div>
          <div className="metric-subtitle">Amount pending</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          customActions={(row) => {
            const actions = [
              { type: 'view', label: 'View Details', onClick: () => handleView(row) },
            ]
            
            // Only show Edit, Approve, and Delete for Pending reimbursements
            if (row.status === 'Pending') {
              actions.push(
                { type: 'edit', label: 'Edit', onClick: () => handleEdit(row) },
                { type: 'approve', label: 'Approve', icon: <CheckCircle size={16} />, onClick: () => handleApprove(row) },
                { type: 'delete', label: 'Delete', onClick: () => handleDelete(row) }
              )
            }
            
            return actions
          }}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Reimbursement"
        message={`Are you sure you want to delete reimbursement "${deleteItem?.reimbursementNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Approve Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={cancelApprove}
        onConfirm={confirmApprove}
        title="Approve Reimbursement"
        message={`Are you sure you want to approve reimbursement "${approveItem?.reimbursementNumber}"? Once approved, it will be processed and removed from this list.`}
        confirmText="Approve"
        cancelText="Cancel"
        type="info"
      />
    </div>
  )
}

export default ExpenseReimbursement

